<template>
  <v-date-picker
    v-model="selectedDate"
    :mode="mode"
    :min-date="minDate"
    :max-date="maxDate"
    :is24hr="is24hr"
    :is-range="isRange"
    :is-expanded="isExpanded"
    :attributes="attributes"
    :minute-increment="minuteIncrement"
    trim-weeks
    color="orange"
    @input="changeValue"
  >
    <template v-slot="{ inputValue, inputEvents }">
      <div>
        <!-- Label -->
        <label :class="labelComputedClasses" :for="id">
          {{ label }}
          <ips-icon
            v-if="tooltip"
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            icon="ico-help"
            :title="tooltip"
          ></ips-icon>
        </label>

        <div :class="formComputedClasses">
          <ips-icon icon="ico-calendar" class="ips-form-icon--prepend"></ips-icon>

          <input
            :id="id"
            :value="isRange ? dateRangInputValue(inputValue) : inputValue"
            type="text"
            :class="inputComputedClasses"
            class="form-control has-icon--prepend"
            :tabindex="tabindex"
            autocomplete="off"
            :placeholder="placeholder"
            v-on="isRange ? inputEvents.start : inputEvents"
          />

          <!-- Clear Icon -->
          <ips-icon
            v-show="value"
            icon="ico-delete"
            class="ips-form-icon--clear"
            @click="clearInputValue"
          ></ips-icon>

          <small v-if="error" class="ips-validation form-text text-danger">
            <ips-icon icon="ico-danger" size="sm"></ips-icon>
            <!-- @slot Use this slot to populate a validation/error message -->
            <slot name="errorText"></slot>
          </small>
        </div>
      </div>
    </template>
  </v-date-picker>
</template>

<script>
import VDatePicker from 'v-calendar/lib/components/date-picker.umd';
import IpsIcon from '@ipscape/ips-icon';
import { formFieldsMixin } from '../../../mixins/index.js';

export default {
  name: 'IpsDatePicker',
  components: { IpsIcon, VDatePicker },
  mixins: [formFieldsMixin],
  props: {
    mode: {
      name: 'mode',
      description: 'The mode of the date picker',
      type: String,
      default: 'date',
      required: false,
      validator: (value) => ['', 'date', 'dateTime', 'time'].indexOf(value) !== -1,
    },
    timezone: {
      name: 'timezone',
      description: 'The timezone of the time',
      type: String,
      default: '',
      required: false,
    },
    minDate: {
      name: 'minDate',
      description: 'Minimum date that can be selected',
      type: Date,
      default: undefined,
      required: false,
    },
    maxDate: {
      name: 'maxDate',
      description: 'Maximum date that can be selected',
      type: Date,
      default: undefined,
      required: false,
    },
    is24hr: {
      name: 'is24hr',
      description: 'Use 24-hr time picker and input format',
      type: Boolean,
      default: false,
      required: false,
    },
    isRange: {
      name: 'isRange',
      description: 'Date value is a date range object',
      type: Boolean,
      default: false,
      required: false,
    },
    minuteIncrement: {
      name: 'minuteIncrement',
      description:
        'Use the minute-increment prop to set custom intervals for the minute select options',
      type: Number,
      default: 1,
      required: false,
    },
    isExpanded: {
      name: 'isExpanded',
      description: 'To expand the component to the full width of its container',
      type: Boolean,
      default: false,
      required: false,
    },
    popoverPlacement: {
      name: 'popoverPlacement',
      description: 'The placement of the date picker popover',
      type: String,
      default: 'bottom-start',
      required: false,
      validator: (value) => ['', 'top-start', 'bottom-start'].indexOf(value) !== -1,
    },
    attributes: {
      name: 'attributes',
      description: 'Visual decorators that can be applied to specific calendar dates.',
      type: Array,
      default: () => {
        return [{}];
      },
      required: false,
    },
  },
  data() {
    return {
      selectedDate: this.mode === 'time' ? new Date() : this.value ? this.value : null,
    };
  },
  methods: {
    dateRangInputValue(inputValue) {
      let rangeString = '';
      if (inputValue && inputValue.start && inputValue.end) {
        rangeString = `${inputValue.start} - ${inputValue.end}`;
      }
      return rangeString;
    },
    changeValue() {
      this.$emit('input', this.selectedDate);
    },
    clearInputValue() {
      this.selectedDate = null;
      this.$emit('input', '');
    },
  },
};
</script>

<style lang="scss">
@import 'IpsDatePicker';
</style>
